import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Button } from 'react-bootstrap';
import AdminLayout from '../layouts/AdminLayout';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { buonoService, BuonoRow, BuonoDetail } from '../../services/api';
import BuonoPdfExport from './BuonoPdfExport';
import './Archivio.css';

type EditForm = {
  regalo: string;
  per: string;
  da: string;
  operatore: string;
  validoFino: string;
  mail: string;
  telefono: string;
};

/** Opzione dropdown operatori: id univoco, name = valore salvato nel buono */
type OperatoreOption = { id: string; name: string };

const STATO_OPTIONS: { value: BuonoRow['stato']; label: string }[] = [
  { value: 'green', label: 'Attivo' },
  { value: 'red', label: 'Scaduto' },
  { value: 'yellow', label: 'Utilizzato' },
];

/** Colonne tabella archivio (ordine uguale all’header) – larghezze ridimensionabili */
const ARCHIVIO_COLUMNS: { key: string; label: string }[] = [
  { key: 'n', label: 'N' },
  { key: 'a', label: 'A' },
  { key: 'da', label: 'Da' },
  { key: 'regalo', label: 'Regalo' },
  { key: 'mail', label: 'Mail' },
  { key: 'telefono', label: 'Tel. cliente' },
  { key: 'operatore', label: 'Operatore' },
  { key: 'creato', label: 'Creato il' },
  { key: 'scade', label: 'Scade il' },
  { key: 'stato', label: 'Stato' },
  { key: 'azioni', label: 'Azioni' },
];

const ARCHIVIO_DEFAULT_WIDTHS = [48, 95, 100, 140, 180, 115, 120, 100, 95, 88, 110];
const ARCHIVIO_COLUMNS_STORAGE_KEY = 'archivio-column-widths';
const ARCHIVIO_MIN_COL_WIDTH = 32;

function loadStoredColumnWidths(): number[] {
  try {
    const raw = localStorage.getItem(ARCHIVIO_COLUMNS_STORAGE_KEY);
    if (!raw) return [...ARCHIVIO_DEFAULT_WIDTHS];
    const parsed = JSON.parse(raw) as number[];
    if (Array.isArray(parsed) && parsed.length === ARCHIVIO_DEFAULT_WIDTHS.length) {
      return parsed.map((w, i) => Math.max(ARCHIVIO_MIN_COL_WIDTH, Number(w) || ARCHIVIO_DEFAULT_WIDTHS[i]));
    }
  } catch (_) {}
  return [...ARCHIVIO_DEFAULT_WIDTHS];
}

const Archivio: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, canEditBuoni, canDeleteBuoni } = useAuth();
  const [showBuonoCreatedSuccess, setShowBuonoCreatedSuccess] = useState(false);
  const [search, setSearch] = useState('');
  const [columnWidths, setColumnWidths] = useState<number[]>(() => loadStoredColumnWidths());
  const [isDesktopTable, setIsDesktopTable] = useState(() => typeof window !== 'undefined' && window.innerWidth > 768);
  const resizingColRef = useRef<number | null>(null);
  const resizingStartXRef = useRef(0);
  const resizingStartWidthRef = useRef(0);
  const [operatorOptions, setOperatorOptions] = useState<OperatoreOption[]>([]);
  const [rows, setRows] = useState<BuonoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [openStatoRowId, setOpenStatoRowId] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  /** Id del buono da eliminare: se non null mostra il modal di conferma (come in Utenti) */
  const [deleteBuonoTarget, setDeleteBuonoTarget] = useState<number | null>(null);
  /** Modifica: id buono da modificare; se non null mostra modal con form */
  const [editBuonoTarget, setEditBuonoTarget] = useState<number | null>(null);
  /** Export PDF da screenshot (come il riferimento): buono da esportare per apertura in nuova scheda; se non null mostra overlay e avvia BuonoPdfExport */
  const [pdfExportBuono, setPdfExportBuono] = useState<BuonoDetail | null>(null);
  /** Export PDF da screenshot per invio via email (flusso html2canvas → PDF allegato) */
  const [shareExportBuono, setShareExportBuono] = useState<BuonoDetail | null>(null);
  const [shareTargetEmail, setShareTargetEmail] = useState<string | null>(null);
  const [sharePdfLoading, setSharePdfLoading] = useState(false);
  const [sharePdfError, setSharePdfError] = useState<string | null>(null);
  const [shareEmailSuccess, setShareEmailSuccess] = useState<string | null>(null);
  const handleDirectShareRef = useRef<(buonoId: number) => void>(() => {});
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const statoMenuRef = useRef<HTMLDivElement | null>(null);
  const statoTriggerRef = useRef<HTMLButtonElement | null>(null);
  const statoMenuPortalRef = useRef<HTMLDivElement | null>(null);
  const cardBodyRef = useRef<HTMLDivElement | null>(null);
  const handleDownloadPdfRef = useRef<(id: number) => void>(() => {});
  const handleDeleteBuonoRef = useRef<(id: number) => void>(() => {});
  const [resizingCol, setResizingCol] = useState<number | null>(null);
  /** Vista compatta: riga con dropdown "Azioni" aperto (solo mobile) */
  const [openMobileActionsRowId, setOpenMobileActionsRowId] = useState<number | null>(null);
  const mobileActionsRef = useRef<HTMLDivElement | null>(null);

  const canEdit = isAdmin() || canEditBuoni();
  const canDelete = isAdmin() || canDeleteBuoni();

  useEffect(() => {
    localStorage.setItem(ARCHIVIO_COLUMNS_STORAGE_KEY, JSON.stringify(columnWidths));
  }, [columnWidths]);

  useEffect(() => {
    const onResize = () => setIsDesktopTable(window.innerWidth > 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /** Mostra "Buono creato correttamente" quando si arriva da Genera Buono → Salva e Crea (state o sessionStorage). */
  useEffect(() => {
    const fromState = (location.state as { buonoCreated?: string } | null)?.buonoCreated === 'success';
    const fromStorage = sessionStorage.getItem('buonoCreatedSuccess') === '1';
    if (fromState || fromStorage) {
      if (fromStorage) sessionStorage.removeItem('buonoCreatedSuccess');
      setShowBuonoCreatedSuccess(true);
    }
  }, [location.state, location.key]);

  const startResize = (e: React.MouseEvent, colIndex: number) => {
    e.preventDefault();
    resizingColRef.current = colIndex;
    resizingStartXRef.current = e.clientX;
    resizingStartWidthRef.current = columnWidths[colIndex];
    setResizingCol(colIndex);
  };

  useEffect(() => {
    if (resizingCol === null) return;
    const onMouseMove = (e: MouseEvent) => {
      if (resizingColRef.current === null) return;
      const delta = e.clientX - resizingStartXRef.current;
      const newW = Math.max(ARCHIVIO_MIN_COL_WIDTH, resizingStartWidthRef.current + delta);
      setColumnWidths((prev) => {
        const next = [...prev];
        next[resizingColRef.current!] = newW;
        return next;
      });
    };
    const onMouseUp = () => {
      resizingColRef.current = null;
      setResizingCol(null);
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
    };
  }, [resizingCol]);

  useLayoutEffect(() => {
    if (openStatoRowId === null) {
      setMenuPosition(null);
      return;
    }
    const trigger = statoTriggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.left + rect.width / 2,
    });
  }, [openStatoRowId]);

  useEffect(() => {
    if (openStatoRowId === null) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = statoMenuRef.current?.contains(target);
      const inMenu = statoMenuPortalRef.current?.contains(target);
      if (!inTrigger && !inMenu) setOpenStatoRowId(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openStatoRowId]);

  useEffect(() => {
    if (openMobileActionsRowId === null) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (mobileActionsRef.current?.contains(target)) return;
      setOpenMobileActionsRowId(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openMobileActionsRowId]);

  // Delegazione clic sulla tabella (capture phase): Scarica ed Elimina
  useEffect(() => {
    const el = cardBodyRef.current;
    if (!el) return;
    const onCardBodyClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const downloadBtn = target.closest?.('button[data-action="download-pdf"]') as HTMLElement | null;
      if (downloadBtn) {
        const id = downloadBtn.getAttribute('data-buono-id');
        if (id) {
          e.preventDefault();
          e.stopPropagation();
          handleDownloadPdfRef.current(Number(id));
        }
        return;
      }
      const deleteBtn = target.closest?.('button[data-action="delete-buono"]') as HTMLElement | null;
      if (deleteBtn) {
        const id = deleteBtn.getAttribute('data-buono-id');
        if (id) {
          e.preventDefault();
          e.stopPropagation();
          handleDeleteBuonoRef.current(Number(id));
        }
        return;
      }
      const shareBtn = target.closest?.('button[data-action="share-buono"]') as HTMLElement | null;
      if (shareBtn) {
        const id = shareBtn.getAttribute('data-buono-id');
        if (id) {
          e.preventDefault();
          e.stopPropagation();
          handleDirectShareRef.current(Number(id));
        }
      }
    };
    el.addEventListener('click', onCardBodyClick, true);
    return () => el.removeEventListener('click', onCardBodyClick, true);
  }, []);

  const handleDownloadPdf = async (buonoId: number) => {
    setDownloadError(null);
    setDownloadingId(buonoId);
    try {
      const buonoDetail = await buonoService.getBuono(buonoId);
      if (!buonoDetail.template) {
        const url = await buonoService.getBuonoPdfUrl(buonoId);
        window.open(url, '_blank');
        return;
      }
      setPdfExportBuono(buonoDetail);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Impossibile aprire il PDF';
      setDownloadError(msg);
      setTimeout(() => setDownloadError(null), 5000);
    } finally {
      setDownloadingId(null);
    }
  };
  handleDownloadPdfRef.current = handleDownloadPdf;

  /** Apre il modal di conferma eliminazione (stesso pattern di Utenti) */
  const handleDeleteBuono = (buonoId: number) => {
    setDeleteError(null);
    setDeleteBuonoTarget(buonoId);
  };
  handleDeleteBuonoRef.current = handleDeleteBuono;

  const handleDirectShare = async (buonoId: number) => {
    setSharePdfError(null);
    setShareEmailSuccess(null);
    setSharePdfLoading(true);
    setShareExportBuono(null);
    setShareTargetEmail(null);
    try {
      const buono = await buonoService.getBuono(buonoId);
      const to = (buono.mail ?? '').trim();
      if (!to) {
        setSharePdfError("Aggiungi l'email nel buono (modifica) per inviare la mail.");
        setSharePdfLoading(false);
        return;
      }
      setShareTargetEmail(to);
      setShareExportBuono(buono);
      // Il PDF verrà generato da BuonoPdfExport e inviato tramite sendShareEmailWithPdf.
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Impossibile preparare il buono per l\'invio email.';
      setSharePdfError(msg);
      setTimeout(() => setSharePdfError(null), 8000);
      setSharePdfLoading(false);
    }
  };
  handleDirectShareRef.current = handleDirectShare;

  const handleConfirmDeleteBuono = async () => {
    if (deleteBuonoTarget == null) return;
    setDeleteError(null);
    setDeletingId(deleteBuonoTarget);
    try {
      await buonoService.deleteBuono(deleteBuonoTarget);
      setRows((prev) => prev.filter((r) => r.id !== deleteBuonoTarget));
      setDeleteBuonoTarget(null);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Impossibile eliminare il buono';
      setDeleteError(msg);
      setTimeout(() => setDeleteError(null), 5000);
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatoChange = async (buonoId: number, nuovoStato: BuonoRow['stato']) => {
    setOpenStatoRowId(null);
    setUpdatingId(buonoId);
    try {
      await buonoService.updateBuono(buonoId, { stato: nuovoStato });
      setRows((prev) =>
        prev.map((r) => (r.id === buonoId ? { ...r, stato: nuovoStato } : r))
      );
    } catch {
      // mantieni stato precedente
    } finally {
      setUpdatingId(null);
    }
  };

  const handleOpenModifica = (buonoId: number) => {
    setEditError(null);
    setEditForm(null);
    setEditBuonoTarget(buonoId);
  };

  useEffect(() => {
    let cancelled = false;
    api.get('/admin/teams').then((res) => {
      if (cancelled) return;
      const teams = res.data || [];
      const list: OperatoreOption[] = [];
      if (isAdmin()) {
        list.push({ id: 'current-user', name: 'Denise Cruciani' });
      }
      teams.forEach((team: { id: number; username?: string; teamMembers?: Array<{ id: number; name: string }>; team_members?: Array<{ id: number; name: string }> }) => {
        const members = team.teamMembers || team.team_members || [];
        const teamName = team.username || '';
        members.forEach((m: { id: number; name: string }) => {
          list.push({
            id: `team-${team.id}-${m.id}`,
            name: teamName ? `${m.name} (${teamName})` : m.name,
          });
        });
      });
      setOperatorOptions(list);
    }).catch(() => {
      if (!cancelled) setOperatorOptions([]);
    });
    return () => { cancelled = true; };
  }, [isAdmin, user?.name]);

  useEffect(() => {
    if (editBuonoTarget == null) {
      setEditForm(null);
      return;
    }
    let cancelled = false;
    setEditLoading(true);
    setEditError(null);
    buonoService
      .getBuono(editBuonoTarget)
      .then((b) => {
        if (cancelled) return;
        const telefonoCliente = (b as { telefono?: string }).telefono ?? b.telefono ?? '';
        setEditForm({
          regalo: b.regalo ?? '',
          per: b.a ?? '',
          da: b.da ?? '',
          operatore: b.operatore ?? '',
          validoFino: b.scade_at ?? '',
          mail: b.mail ?? '',
          telefono: telefonoCliente,
        });
      })
      .catch((err: any) => {
        if (!cancelled) {
          setEditError(err.response?.data?.message || err.message || 'Impossibile caricare il buono');
        }
      })
      .finally(() => {
        if (!cancelled) setEditLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editBuonoTarget]);

  const handleSubmitModifica = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editBuonoTarget == null || editForm == null) return;
    setEditError(null);
    setEditSaving(true);
    try {
      const payload: Record<string, string | undefined> = {
        regalo: editForm.regalo || undefined,
        a: editForm.per || undefined,
        da: editForm.da || undefined,
        operatore: editForm.operatore.trim() || undefined,
        scade_at: editForm.validoFino ? `${editForm.validoFino}-01` : undefined,
        mail: editForm.mail.trim() || undefined,
        telefono: (editForm.telefono ?? '').toString().trim(),
      };
      const res = await buonoService.updateBuono(editBuonoTarget, payload);
      setRows((prev) =>
        prev.map((r) => (r.id === editBuonoTarget ? { ...r, ...res.buono } : r))
      );
      setEditBuonoTarget(null);
      setEditForm(null);
    } catch (err: any) {
      setEditError(err.response?.data?.message || err.message || 'Impossibile aggiornare il buono');
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await buonoService.getBuoni(search || undefined);
        if (!cancelled) setRows(data);
      } catch (err: any) {
        if (cancelled) return;
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          setError('Timeout: il server non risponde. Verifica che il backend sia avviato (es. php artisan serve su localhost:8000).');
        } else if (err.response?.status === 401) {
          setError('Sessione scaduta. Esegui di nuovo l’accesso.');
        } else {
          setError(err.response?.data?.message || err.message || 'Errore nel caricamento dell’archivio');
        }
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [search]);

  return (
    <AdminLayout activeSection="archivio">
      <div className="archive-page">
        <div className="archive-header">
          <h1>Archivio</h1>
          <p>Gestisci i buoni regalo</p>
        </div>

        <div className="archive-search">
          <span className="archive-search-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M7.333 12.0002C9.91033 12.0002 12.0003 9.9102 12.0003 7.33285C12.0003 4.75551 9.91033 2.66553 7.333 2.66553C4.75566 2.66553 2.66569 4.75551 2.66569 7.33285C2.66569 9.9102 4.75566 12.0002 7.333 12.0002Z" stroke="#99A1AF" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12.6667 12.6667L10.3334 10.3334" stroke="#99A1AF" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          <input
            type="text"
            placeholder="Cerca per nome, location o servizio..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="archive-legend">
          <div className="archive-legend-item">
            <span className="archive-status green" />
            <span>Attivo</span>
          </div>
          <div className="archive-legend-item">
            <span className="archive-status red" />
            <span>Scaduto</span>
          </div>
          <div className="archive-legend-item">
            <span className="archive-status yellow" />
            <span>Utilizzato</span>
          </div>
          {isDesktopTable && (
            <button
              type="button"
              className="archive-reset-columns-btn"
              onClick={() => setColumnWidths([...ARCHIVIO_DEFAULT_WIDTHS])}
              title="Ripristina larghezze colonne predefinite"
            >
              Ripristina larghezze colonne
            </button>
          )}
        </div>

        <div className="archive-card">
          <div className="archive-table-scroll">
            <div
              className={`archive-card-header archive-card-header--resizable${resizingCol !== null ? ' resizing' : ''}`}
              style={
                isDesktopTable
                  ? {
                      gridTemplateColumns: columnWidths.map((w) => `${w}px`).join(' '),
                      minWidth: columnWidths.reduce((a, b) => a + b, 0) + 10 * 12 + 40,
                    }
                  : undefined
              }
            >
              {ARCHIVIO_COLUMNS.map((col, i) => (
                <div key={col.key} className={`archive-col ${col.key} archive-col--header`}>
                  <span className="archive-col-label">{col.label}</span>
                  {isDesktopTable && (
                    <div
                      className="archive-col-resizer"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Ridimensiona colonna ${col.label}`}
                      onMouseDown={(e) => startResize(e, i)}
                    />
                  )}
                </div>
              ))}
            </div>

            <div
              className="archive-card-body"
              ref={cardBodyRef}
              style={
                isDesktopTable
                  ? { minWidth: columnWidths.reduce((a, b) => a + b, 0) + 10 * 12 + 40 }
                  : undefined
              }
            >
            {downloadError && <div className="archive-error archive-download-error">{downloadError}</div>}
            {deleteError && <div className="archive-error archive-delete-error">{deleteError}</div>}
            {sharePdfError && <div className="archive-error archive-share-error">{sharePdfError}</div>}
            {sharePdfLoading && <div className="archive-loading">Invio email in corso...</div>}
            {loading && <div className="archive-loading">Caricamento...</div>}
            {error && <div className="archive-error">{error}</div>}
            {!loading && !error && rows.map((row, idx) => (
              <div
                key={`${row.id}-${idx}`}
                className="archive-row"
                style={
                  isDesktopTable
                    ? { gridTemplateColumns: columnWidths.map((w) => `${w}px`).join(' ') }
                    : undefined
                }
              >
                <div className="archive-row-head">
                  <div className="archive-row-id">
                    <span className="archive-row-id-label">N.</span>
                    <span className="archive-row-id-value">{row.id}</span>
                    <span className={`archive-status ${row.stato}`} />
                  </div>
                  <div
                    className="archive-mobile-actions-wrap"
                    ref={openMobileActionsRowId === row.id ? mobileActionsRef : undefined}
                  >
                    <button
                      type="button"
                      className="archive-mobile-actions-trigger"
                      aria-label="Azioni"
                      aria-expanded={openMobileActionsRowId === row.id}
                      aria-haspopup="menu"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setOpenMobileActionsRowId((id) => (id === row.id ? null : row.id));
                      }}
                    >
                      Azioni
                      <span className="archive-mobile-actions-chevron" aria-hidden>▾</span>
                    </button>
                    {openMobileActionsRowId === row.id && (
                      <div className="archive-mobile-actions-menu" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          disabled={downloadingId === row.id}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDownloadPdfRef.current(row.id);
                            setOpenMobileActionsRowId(null);
                          }}
                        >
                          Apri PDF
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDirectShareRef.current(row.id);
                            setOpenMobileActionsRowId(null);
                          }}
                        >
                          Invia email
                        </button>
                        {canEdit && (
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleOpenModifica(row.id);
                              setOpenMobileActionsRowId(null);
                            }}
                          >
                            Modifica
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            role="menuitem"
                            disabled={deletingId === row.id}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteBuono(row.id);
                              setOpenMobileActionsRowId(null);
                            }}
                          >
                            Elimina
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="archive-col n" data-label="N">{row.id}</div>
                <div className="archive-col a" data-label="A">{row.a}</div>
                <div className="archive-col da" data-label="Da">{row.da}</div>
                <div className="archive-col regalo" data-label="Regalo">{row.regalo}</div>
                <div className="archive-col mail" data-label="Mail">{row.mail}</div>
                <div className="archive-col telefono" data-label="Tel. cliente">{row.telefono ?? ''}</div>
                <div className="archive-col operatore" data-label="Operatore">{row.operatore}</div>
                <div className="archive-col creato" data-label="Creato il">{row.creato}</div>
                <div className="archive-col scade" data-label="Scade il">{row.scade}</div>
                <div className="archive-col stato" data-label="Stato" ref={row.id === openStatoRowId ? statoMenuRef : undefined}>
                  {canEdit ? (
                    <div className="archive-stato-dropdown">
                      <button
                        ref={openStatoRowId === row.id ? statoTriggerRef : undefined}
                        type="button"
                        className="archive-stato-trigger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenStatoRowId((id) => (id === row.id ? null : row.id));
                        }}
                        disabled={updatingId === row.id}
                        aria-label={`Stato: ${STATO_OPTIONS.find((o) => o.value === row.stato)?.label ?? row.stato}`}
                        aria-expanded={openStatoRowId === row.id}
                        aria-haspopup="listbox"
                      >
                        <span className={`archive-status ${row.stato}`} />
                        <span className="archive-stato-chevron" aria-hidden>▾</span>
                      </button>
                    </div>
                  ) : (
                    <span className={`archive-status ${row.stato}`} />
                  )}
                </div>
                <div className="archive-col azioni" data-label="Azioni">
                  {(canEdit || canDelete) ? (
                    <div className="archive-actions">
                      <button
                        type="button"
                        data-action="download-pdf"
                        data-buono-id={row.id}
                        aria-label="Apri PDF"
                        disabled={downloadingId === row.id}
                        title="Apri il PDF del buono in una nuova scheda"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 3v7M4.5 6.5L8 10l3.5-3.5" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M3 12.5a2.5 2.5 0 0 0 2.5 2.5h5A2.5 2.5 0 0 0 13 12.5" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button
                        type="button"
                        data-action="share-buono"
                        data-buono-id={row.id}
                        aria-label="Condividi"
                        title="Condividi buono"
                        className="archive-action-btn-share"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDirectShareRef.current(row.id);
                        }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 82.003 82.003"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.266,70.501h73.469c2.352,0,4.265-2.033,4.265-4.386V15.666c0-0.009,0.003,0.043,0.003,0.034 c0-2.348-1.914-4.199-4.267-4.199h-0.002H4.612H4.265C1.913,11.501,0,13.296,0,15.648v50.467C0,68.468,1.913,70.501,4.266,70.501z M29.525,38.624L4,63.944V19.391L29.525,38.624z M78,19.688v42.289L53.914,38.083L78,19.688z M50.723,40.521 c0.044,0.053,0.082,0.169,0.132,0.22l25.909,25.76H7.221l25.533-25.387l7.481,5.607c0.356,0.268,0.779,0.388,1.203,0.388 c0.428,0,0.855-0.144,1.214-0.418L50.723,40.521z M77.047,15.501L41.431,42.703L5.326,15.501H77.047z"
                            fill="#0A0A0A"
                          />
                        </svg>
                      </button>
                      {canEdit && (
                        <button
                          type="button"
                          aria-label="Modifica"
                          title="Modifica buono"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpenModifica(row.id); }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12.2504 1.75015C12.5156 1.48493 12.8753 1.33594 13.2504 1.33594C13.6255 1.33594 13.9852 1.48493 14.2504 1.75015C14.5156 2.01537 14.6646 2.37508 14.6646 2.75015C14.6646 3.12522 14.5156 3.48493 14.2504 3.75015L8.24172 9.75948C8.08342 9.91765 7.88786 10.0334 7.67305 10.0962L5.75772 10.6562C5.70036 10.6729 5.63955 10.6739 5.58166 10.6591C5.52377 10.6442 5.47094 10.6141 5.42869 10.5719C5.38643 10.5296 5.35631 10.4768 5.34148 10.4189C5.32665 10.361 5.32766 10.3002 5.34439 10.2428L5.90439 8.32748C5.96741 8.11285 6.08341 7.91752 6.24172 7.75948L12.2504 1.75015Z" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          data-action="delete-buono"
                          data-buono-id={row.id}
                          aria-label="Elimina"
                          disabled={deletingId === row.id}
                          title="Elimina buono"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteBuono(row.id); }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 4H14" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12.6663 4V13.3333C12.6663 14 11.9997 14.6667 11.333 14.6667H4.66634C3.99967 14.6667 3.33301 14 3.33301 13.3333V4" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5.33301 4.00016V2.66683C5.33301 2.00016 5.99967 1.3335 6.66634 1.3335H9.33301C9.99967 1.3335 10.6663 2.00016 10.6663 2.66683V4.00016" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M6.66699 7.3335V11.3335" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9.33301 7.3335V11.3335" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="archive-actions">
                      <button
                        type="button"
                        data-action="download-pdf"
                        data-buono-id={row.id}
                        aria-label="Apri PDF"
                        disabled={downloadingId === row.id}
                        title="Apri il PDF del buono in una nuova scheda"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 3v7M4.5 6.5L8 10l3.5-3.5" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M3 12.5a2.5 2.5 0 0 0 2.5 2.5h5A2.5 2.5 0 0 0 13 12.5" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button
                        type="button"
                        data-action="share-buono"
                        data-buono-id={row.id}
                        aria-label="Condividi"
                        title="Condividi buono"
                        className="archive-action-btn-share"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDirectShareRef.current(row.id);
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 82.003 82.003"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.266,70.501h73.469c2.352,0,4.265-2.033,4.265-4.386V15.666c0-0.009,0.003,0.043,0.003,0.034 c0-2.348-1.914-4.199-4.267-4.199h-0.002H4.612H4.265C1.913,11.501,0,13.296,0,15.648v50.467C0,68.468,1.913,70.501,4.266,70.501z M29.525,38.624L4,63.944V19.391L29.525,38.624z M78,19.688v42.289L53.914,38.083L78,19.688z M50.723,40.521 c0.044,0.053,0.082,0.169,0.132,0.22l25.909,25.76H7.221l25.533-25.387l7.481,5.607c0.356,0.268,0.779,0.388,1.203,0.388 c0.428,0,0.855-0.144,1.214-0.418L50.723,40.521z M77.047,15.501L41.431,42.703L5.326,15.501H77.047z"
                            fill="#0A0A0A"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          </div>

          <div className="archive-pagination">
            <span>Pagina 1 di 1</span>
            <div className="archive-page-controls">
              <button aria-label="Prev">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 4l-4 4 4 4" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <button className="active">1</button>
              <button aria-label="Next">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4l4 4-4 4" stroke="#0A0A0A" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="archive-footer">
          <span>© 2021 All rights reserved Estetica Cruciani</span>
        </div>

        {openStatoRowId != null && menuPosition != null &&
          createPortal(
            <div
              ref={statoMenuPortalRef}
              className="archive-stato-menu archive-stato-menu--portal"
              style={{
                position: 'fixed',
                top: menuPosition.top,
                left: menuPosition.left,
                transform: 'translateX(-50%)',
              }}
              role="listbox"
              aria-label="Scegli stato"
            >
              {STATO_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={rows.find((r) => r.id === openStatoRowId)?.stato === opt.value}
                  className="archive-stato-option"
                  title={opt.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatoChange(openStatoRowId, opt.value);
                  }}
                >
                  <span className={`archive-status ${opt.value}`} />
                </button>
              ))}
            </div>,
            document.body
          )}

        {/* Modal modifica buono */}
        <Modal
          show={editBuonoTarget != null}
          onHide={() => { setEditBuonoTarget(null); setEditError(null); }}
          centered
          className="edit-buono-modal"
        >
          <div className="edit-buono-box">
            <div className="edit-buono-header">
              <h2 className="edit-buono-title">Modifica buono</h2>
              <button
                type="button"
                className="edit-buono-close"
                onClick={() => { setEditBuonoTarget(null); setEditError(null); }}
                aria-label="Chiudi"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 2l12 12M14 2L2 14" />
                </svg>
              </button>
            </div>
            {editLoading && <div className="edit-buono-loading">Caricamento...</div>}
            {editError && <div className="edit-buono-error">{editError}</div>}
            {!editLoading && editForm && (
              <form onSubmit={handleSubmitModifica} className="edit-buono-form">
                <div className="edit-buono-field">
                  <label htmlFor="edit-regalo">Regalo (max 100 caratteri)</label>
                  <input
                    id="edit-regalo"
                    type="text"
                    value={editForm.regalo}
                    maxLength={100}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, regalo: e.target.value } : f))}
                    placeholder="Es. Trattamento viso"
                  />
                  <div className="edit-buono-char-counter">
                    {editForm.regalo.length}/100
                  </div>
                </div>
                <div className="edit-buono-field">
                  <label htmlFor="edit-per">Per (max 50 caratteri)</label>
                  <input
                    id="edit-per"
                    type="text"
                    value={editForm.per}
                    maxLength={50}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, per: e.target.value } : f))}
                    placeholder="Destinatario"
                  />
                  <div className="edit-buono-char-counter">
                    {editForm.per.length}/50
                  </div>
                </div>
                <div className="edit-buono-field">
                  <label htmlFor="edit-da">Da (max 50 caratteri)</label>
                  <input
                    id="edit-da"
                    type="text"
                    value={editForm.da}
                    maxLength={50}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, da: e.target.value } : f))}
                    placeholder="Mittente"
                  />
                  <div className="edit-buono-char-counter">
                    {editForm.da.length}/50
                  </div>
                </div>
                <div className="edit-buono-field">
                  <label htmlFor="edit-operatore">Operatore</label>
                  <select
                    id="edit-operatore"
                    value={editForm.operatore}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, operatore: e.target.value } : f))}
                    required
                    className="edit-buono-select"
                  >
                    <option value="">Seleziona operatore *</option>
                    {operatorOptions.some((o) => o.name === editForm.operatore) ? null : (
                      editForm.operatore ? <option value={editForm.operatore}>{editForm.operatore}</option> : null
                    )}
                    {operatorOptions.map((o) => (
                      <option key={o.id} value={o.name}>{o.name}</option>
                    ))}
                  </select>
                </div>
                <div className="edit-buono-field">
                  <label htmlFor="edit-valido">Valido fino a (mese/anno)</label>
                  <input
                    id="edit-valido"
                    type="month"
                    value={editForm.validoFino}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, validoFino: e.target.value } : f))}
                  />
                </div>
                <div className="edit-buono-field">
                  <label htmlFor="edit-mail">Mail</label>
                  <input
                    id="edit-mail"
                    type="email"
                    value={editForm.mail}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, mail: e.target.value } : f))}
                    placeholder="email@esempio.it"
                  />
                </div>
                <div className="edit-buono-field">
                  <label htmlFor="edit-telefono">Telefono (cliente)</label>
                  <input
                    id="edit-telefono"
                    type="tel"
                    value={editForm.telefono ?? ''}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, telefono: e.target.value } : f))}
                    placeholder="Numero del cliente a cui è destinato il buono"
                  />
                </div>
                <div className="edit-buono-footer">
                  <Button
                    type="button"
                    variant="light"
                    className="edit-buono-btn-cancel"
                    onClick={() => { setEditBuonoTarget(null); setEditError(null); }}
                    disabled={editSaving}
                  >
                    Annulla
                  </Button>
                  <Button type="submit" className="edit-buono-btn-save" disabled={editSaving}>
                    {editSaving ? 'Salvataggio...' : 'Salva'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Modal>

        {/* Modal conferma eliminazione buono (stesso stile di Utenti) */}
        <Modal
          show={deleteBuonoTarget != null}
          onHide={() => setDeleteBuonoTarget(null)}
          centered
          className="confirm-delete-modal"
        >
          <div className="confirm-delete-box">
            <div className="confirm-delete-header">
              <h2 className="confirm-delete-title">
                Sei sicuro di voler eliminare questo buono? L&apos;operazione non si può annullare.
              </h2>
              <button
                type="button"
                className="confirm-delete-close"
                onClick={() => setDeleteBuonoTarget(null)}
                aria-label="Chiudi"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 2l12 12M14 2L2 14" />
                </svg>
              </button>
            </div>
            <div className="confirm-delete-footer">
              <Button
                type="button"
                variant="light"
                className="confirm-delete-btn-cancel"
                onClick={() => setDeleteBuonoTarget(null)}
                disabled={deletingId != null}
              >
                Annulla
              </Button>
              <Button
                type="button"
                className="confirm-delete-btn-confirm"
                onClick={handleConfirmDeleteBuono}
                disabled={deletingId != null}
              >
                {deletingId != null ? 'Eliminazione...' : 'Elimina'}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Export PDF da screenshot (stessa anteprima che vedi in Genera Buono → come il PDF di riferimento) per apertura in nuova scheda */}
        {pdfExportBuono != null && (
          <BuonoPdfExport
            buono={pdfExportBuono}
            filename={`buono-regalo-${pdfExportBuono.id}.pdf`}
            onDone={() => setPdfExportBuono(null)}
            onDoneWithBlob={(blob) => {
              const url = URL.createObjectURL(blob);
              window.open(url, '_blank');
              setTimeout(() => URL.revokeObjectURL(url), 5000);
              setPdfExportBuono(null);
            }}
          />
        )}

        {/* Export PDF da screenshot per invio email: genera il PDF con html2canvas e lo invia tramite sendShareEmailWithPdf */}
        {shareExportBuono != null && shareTargetEmail && (
          <BuonoPdfExport
            buono={shareExportBuono}
            filename={`buono-regalo-${shareExportBuono.id}.pdf`}
            onDone={() => {
              setSharePdfLoading(false);
              setShareExportBuono(null);
              setShareTargetEmail(null);
            }}
            scaleOverrideForPdf={4}
            onDoneWithBlob={async (blob) => {
              try {
                const to = shareTargetEmail;
                if (!to) {
                  setSharePdfError("Indirizzo email destinatario mancante o non valido.");
                  setTimeout(() => setSharePdfError(null), 8000);
                  return;
                }
                const response = await buonoService.sendShareEmailWithPdf(blob, to, shareExportBuono.id);
                setShareEmailSuccess(response.message || 'Email inviata con successo.');
                setTimeout(() => setShareEmailSuccess(null), 5000);
              } catch (err: any) {
                const msg =
                  err.response?.data?.message ||
                  err.message ||
                  'Impossibile inviare l\'email. Verifica SMTP (vedi MAIL-SETUP.md).';
                setSharePdfError(msg);
                setTimeout(() => setSharePdfError(null), 8000);
              } finally {
                setSharePdfLoading(false);
                setShareExportBuono(null);
                setShareTargetEmail(null);
              }
            }}
          />
        )}

        {/* Modal successo invio email (con allegato) */}
        {shareEmailSuccess && (
          <div
            className="archive-result-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-share-success-title"
          >
            <div className="archive-result-modal archive-result-modal-success">
              <h3
                id="archive-share-success-title"
                className="archive-result-modal-title"
              >
                {shareEmailSuccess}
              </h3>
              <button
                type="button"
                className="archive-result-modal-btn"
                onClick={() => setShareEmailSuccess(null)}
                aria-label="Chiudi"
              >
                Chiudi
              </button>
            </div>
          </div>
        )}

        {/* Modal "Buono creato correttamente" (verde) dopo redirect da Genera Buono */}
        {showBuonoCreatedSuccess && (
          <div
            className="archive-result-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-buono-created-title"
          >
            <div className="archive-result-modal archive-result-modal-success">
              <h3
                id="archive-buono-created-title"
                className="archive-result-modal-title"
              >
                Buono creato correttamente
              </h3>
              <button
                type="button"
                className="archive-result-modal-btn"
                onClick={() => {
                  setShowBuonoCreatedSuccess(false);
                  sessionStorage.removeItem('buonoCreatedSuccess');
                  navigate('/admin/archivio', { replace: true });
                }}
                aria-label="Chiudi"
              >
                Chiudi
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Archivio;
